
import { supabase } from "@/integrations/supabase/client";
import { Message } from "../message-item";
import { nanoid } from "nanoid";

export interface AssistantResponse {
  response: string;
  threadId: string;
  hasPrivacyWarning?: boolean;
  isProcessingLargeDataset?: boolean;
}

// ======================================================================================
//   NOTE: This type is duplicated in both:
//     - supabase/functions/openai-assistant/index.ts
//     - src/components/chat/api/assistant-api.ts
//   KEEP THESE IN SYNC! Update both if you change this type.
// ======================================================================================
export type BodyType = {
  messages: { role: string; content: string }[];
  threadId: string;
  options?: {
    model?: string;       // Optional model override, should be a valid OpenAI model name
    temperature?: number; // Optional temperature setting for response variability (0.0 to 1.0)
    top_p?: number;       // Optional top_p setting for response variability (0.0 to 1.0)
  };
};

export const callAssistant = async (
  message: string,
  threadId: string | null,
  messages: Message[],
  model?: string,
  temperature?: number,
  top_p?: number
): Promise<AssistantResponse> => {
  console.log('🐛 ASSISTANT API DEBUG STEP 1: Preparing request');
  
  // Generate new thread ID if none exists
  const currentThreadId = threadId || `thread_${nanoid()}`;
  console.log('🧵 [ASSISTANT-API] Thread ID:', currentThreadId, threadId ? '(existing)' : '(new)');
  
  try {
    // Convert messages to the expected format for the edge function
    const formattedMessages = messages.map(msg => ({
      role: msg.role,
      content: msg.content
    }));

    console.log('🐛 ASSISTANT API DEBUG STEP 2: Messages array length:', formattedMessages.length);
    console.log('🐛 ASSISTANT API DEBUG STEP 3: COMPLETE REQUEST PAYLOAD:', {
      messages: formattedMessages
    });

    // Debug message breakdown
    const messageBreakdown = formattedMessages.reduce((acc, msg, index) => {
      acc[`Message ${index + 1} (${msg.role})`] = msg.content.substring(0, 100) + (msg.content.length > 100 ? '...' : '');
      return acc;
    }, {} as Record<string, string>);
    
    console.log('🐛 ASSISTANT API DEBUG STEP 4: MESSAGE BREAKDOWN:', messageBreakdown);

    // Check for system instructions
    const hasSystemInstructions = formattedMessages.some(msg => 
      msg.role === 'system' && (msg.content === 'SYSTEM_INSTRUCTIONS' || msg.content.includes('SYSTEM_INSTRUCTIONS'))
    );
    
    if (hasSystemInstructions) {
      console.log('🐛 ASSISTANT API DEBUG STEP 5: ✅ SYSTEM CONTEXT INCLUDED!');
      const systemMsg = formattedMessages.find(msg => msg.role === 'system');
      console.log('🐛 ASSISTANT API DEBUG STEP 5a: System message length:', systemMsg?.content.length);
      console.log('🐛 ASSISTANT API DEBUG STEP 5b: System message preview:', systemMsg?.content.substring(0, 50) + '...');
    } else {
      console.log('🐛 ASSISTANT API DEBUG STEP 6: ⚠️ NO SYSTEM CONTEXT - this may affect response quality');
    }

    console.log('🐛 ASSISTANT API DEBUG STEP 7: Calling Supabase edge function...');

    const body: BodyType = {
      messages: formattedMessages,
      threadId: currentThreadId,
    };

    if (temperature !== undefined || top_p !== undefined || model !== undefined) {
      body.options = {};
      if (temperature !== undefined) {
        body.options.temperature = temperature;
      }
      if (top_p !== undefined) {
        body.options.top_p = top_p;
      }
      if (model !== undefined) {
        body.options.model = model;
      }
    }

    const { data, error } = await supabase.functions.invoke("openai-assistant", { body });
    if (error) {
      console.error('🐛 ASSISTANT API DEBUG STEP 8: ❌ SUPABASE ERROR:', error);
      throw new Error(`Assistant API error: ${error.message}`);
    }

    if (!data) {
      console.error('🐛 ASSISTANT API DEBUG STEP 9: ❌ NO DATA RETURNED');
      throw new Error('No response from assistant');
    }

    console.log('🐛 ASSISTANT API DEBUG STEP 10: Raw response data:', data);
    console.log('🐛 ASSISTANT API DEBUG STEP 11: ✅ SUCCESSFUL RESPONSE:', typeof data);

    // Extract response content
    let responseContent = '';
    
    if (typeof data === 'string') {
      responseContent = data;
    } else if (data.response) {
      responseContent = data.response;
    } else if (data.content) {
      responseContent = data.content;
    } else {
      console.warn('🐛 ASSISTANT API DEBUG STEP 12: ⚠️ UNEXPECTED RESPONSE FORMAT');
      responseContent = 'Beklager, jeg fikk et uventet svar format.';
    }

    console.log('🧵 [ASSISTANT-API] Returning thread ID:', currentThreadId);

    return {
      response: responseContent,
      threadId: currentThreadId,
      hasPrivacyWarning: data.hasPrivacyWarning,
      isProcessingLargeDataset: data.isProcessingLargeDataset
    };

  } catch (error) {
    console.error('🐛 ASSISTANT API DEBUG STEP 13: ❌ EXCEPTION:', error);
    throw error;
  }
};
